import type { Language } from '@magicschool/supabase/types';
import config from '@/config/frontend';
import { OPENAI_LANGUAGE_CHARACTER_TOKEN_MULTIPLIERS } from '@/features/languages/constants';

export interface WordLimits {
  maxWordsPerDocument: number;
  maxWordsPerGeneration: number;
  maxCharactersPerDocument: number;
}

/**
 * Get language-specific word limits using multipliers, falling back to default limits if not specified
 * @param language - The language code (e.g., 'en-us', 'zh-cn', 'ja')
 * @returns WordLimits object with language-specific or default limits
 */
export function getLanguageSpecificWordLimits(language: Language): WordLimits {
  const { textExtract } = config;
  const multiplier = OPENAI_LANGUAGE_CHARACTER_TOKEN_MULTIPLIERS[language] ?? 1;

  return {
    maxWordsPerDocument: Math.floor(textExtract.maxWordsPerDocument * multiplier),
    maxWordsPerGeneration: Math.floor(textExtract.maxWordsPerGeneration * multiplier),
    maxCharactersPerDocument: Math.floor(textExtract.maxCharactersPerDocument * multiplier),
  };
}

/**
 * Get language-specific word limit for documents
 * @param language - The language code
 * @returns Maximum words per document for the language
 */
export function getLanguageSpecificDocumentWordLimit(language: Language): number {
  return getLanguageSpecificWordLimits(language).maxWordsPerDocument;
}

/**
 * Get language-specific word limit for generation
 * @param language - The language code
 * @returns Maximum words per generation for the language
 */
export function getLanguageSpecificGenerationWordLimit(language: Language): number {
  return getLanguageSpecificWordLimits(language).maxWordsPerGeneration;
}
